import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle, Building2, ChevronDown, ChevronUp } from "lucide-react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero Section - Old UI Style
const HeroSection = () => {
  return (
    <section className="relative bg-slate-900 text-white overflow-hidden" style={{ height: "90vh" }} data-testid="hero-section">
      {/* Background Image */}
      <div 
        className="absolute inset-0 w-full h-full bg-cover bg-center"
        style={{ backgroundImage: "url('/images/assets/hero-scaffolding.jpeg')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/90" />

      {/* Content */}
      <div className="relative h-full flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-full text-yellow-300 text-sm font-medium mb-6 animate-fade-in">
            <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2 animate-pulse" />
            30+ Years of Excellence
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight animate-fade-in" data-testid="hero-headline">
            No Delays. No Capital Block. <br />
            <span className="text-yellow-400">Just Reliable Scaffolding.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto animate-fade-in" data-testid="hero-subheadline">
            Scaffolding rentals for large projects - Delivered fast, engineered for safety, and designed to keep your project moving without unnecessary costs.
          </p>

          {/* Locations */}
          <p className="text-lg text-slate-400 mb-10 animate-fade-in">
            Delhi | Noida | Hyderabad | Vizag
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
            <Link
              to="/contact"
              className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-xl inline-flex items-center justify-center"
              data-testid="hero-cta-quote"
            >
              Get Free Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/projects"
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center justify-center"
              data-testid="hero-cta-projects"
            >
              View Projects
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = ({ stats }) => {
  const statItems = [
    { value: stats?.projects_completed || 141, label: "Projects Completed" },
    { value: "30+", label: "Years Experience" },
    { value: stats?.satisfied_clients || 221, label: "Satisfied Clients" },
    { value: "140+", label: "Workers Employed" },
  ];

  return (
    <section className="py-20 bg-white relative" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {statItems.map((stat, index) => (
            <div key={index} className="text-center group" data-testid={`stat-${index}`}>
              <div className="text-5xl md:text-6xl font-bold text-yellow-500 mb-2 group-hover:scale-110 transition-transform">
                {stat.value}
              </div>
              <div className="text-slate-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section className="py-20 bg-slate-50" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-yellow-500 font-semibold text-sm tracking-wider uppercase">About Us</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4 mb-6">
              Trusted by Builders.<br />
              <span className="text-yellow-500">Proven on Site</span>
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              With 30 years of on-ground construction experience, Amar Preet Group is a partner trusted by builders for consistent quality, on time delivery, and dependable support on demanding sites.
            </p>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              From high-rise to large commercial projects, we've earned our reputation by delivering robust systems, timely mobilization, and service standards that contractors rely on—project after project, generation after generation.
            </p>

            <div className="space-y-3 mb-8">
              {[
                "Well equipped infrastructure for production",
                "Efficient platforms for construction and industrial maintenance",
                "Stringent quality standards of international standards"
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <Link
              to="/about"
              className="inline-flex items-center text-yellow-600 font-semibold hover:text-yellow-700 transition-colors group"
              data-testid="about-learn-more"
            >
              Learn More About Us
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="relative">
            <img
              src="/images/assets/about-team.jpeg"
              alt="About Amar Preet Scaffolding"
              className="rounded-2xl shadow-2xl"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/images/assets/hero-scaffolding.jpeg";
              }}
            />
            <div className="absolute -bottom-6 -left-6 bg-yellow-500 text-slate-900 p-6 rounded-xl shadow-xl">
              <div className="text-3xl font-bold">6 Awards</div>
              <div className="text-sm">Winning Company</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = ({ services }) => {
  return (
    <section className="py-20 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-yellow-500 font-semibold text-sm tracking-wider uppercase">Our Services</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4 mb-4">
            Professional Scaffolding Solutions
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Latest Technology, Machines & Equipment for all types of construction projects
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service) => (
            <Link
              key={service.id}
              to={`/${service.slug}`}
              className="group bg-slate-50 rounded-2xl p-8 hover:bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-transparent hover:border-yellow-500"
              data-testid={`service-card-${service.slug}`}
            >
              <div className="w-20 h-20 bg-yellow-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform overflow-hidden">
                {service.image ? (
                  <img src={service.image} alt={service.name} className={`w-full h-full object-contain ${service.image_style === 'light' ? 'mix-blend-multiply' : 'invert mix-blend-multiply'}`} />
                ) : (
                  <Building2 className="w-8 h-8 text-slate-900" />
                )}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{service.name}</h3>
              <p className="text-slate-600 mb-4">{service.short_description}</p>
              <ul className="space-y-2">
                {service.features.slice(0, 3).map((feature) => (
                  <li key={feature} className="text-sm text-slate-600 flex items-center">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-2" />
                    {feature}
                  </li>
                ))}
              </ul>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/services"
            className="inline-flex items-center bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-8 py-4 rounded-lg font-semibold transition-all duration-300 hover:scale-105"
            data-testid="services-view-all"
          >
            View All Services
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = ({ projects }) => {
  const featuredProjects = projects.slice(0, 6);

  return (
    <section className="py-20 bg-slate-50" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-yellow-500 font-semibold text-sm tracking-wider uppercase">Portfolio</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4 mb-4">Recent Projects</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {featuredProjects.map((project) => (
            <div
              key={project.id}
              className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              data-testid={`project-card-${project.id}`}
            >
              <img
                src={project.image}
                alt={`${project.name} - ${project.category}`}
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent flex flex-col justify-end p-6">
                <h3 className="text-white font-bold text-xl mb-2">{project.name}</h3>
                <p className="text-slate-300 text-sm">{project.location} • {project.category}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/projects"
            className="inline-flex items-center border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-500 hover:text-slate-900 px-8 py-4 rounded-lg font-semibold transition-all duration-300"
            data-testid="projects-view-all"
          >
            View All Projects
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Clients Section
const ClientsSection = ({ clients }) => {
  return (
    <section className="py-20 bg-white" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Trusted by Leading Companies</h2>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-8 items-center opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all">
          {clients.map((client) => (
            <div key={client.id} className="flex items-center justify-center">
              <img
                src={client.logo}
                alt={client.name}
                className="max-h-16 w-auto"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "What types of scaffolding do you offer for rent?",
      answer: "We offer comprehensive scaffolding solutions including Cuplock systems, MS pipes & tubes, steel plates & planks, couplers & joints, adjustable props & jacks, and Acrospan formwork systems."
    },
    {
      question: "Which cities do you serve?",
      answer: "We serve in all cities across India, having our inventory and operations in Delhi, Noida, Hyderabad & Vizag."
    },
    {
      question: "What is the minimum rental period?",
      answer: "The minimum rental period is 3 months. We offer competitive pricing for long-term rentals."
    },
    {
      question: "Do you provide installation support?",
      answer: "We do not provide installation support. We only supply the scaffolding material on rent. Your team or contractor handles the erection and dismantling on site."
    },
    {
      question: "How quickly can you deliver?",
      answer: "With multi-city inventory, we typically mobilize within 24-48 hours in our primary service areas. For large projects, we recommend advance booking for optimal scheduling."
    }
  ];

  return (
    <section className="py-20 bg-slate-50" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-yellow-500 font-semibold text-sm tracking-wider uppercase">FAQ</span>
          <h2 className="text-4xl font-bold text-slate-900 mt-4">Frequently Asked Questions</h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`faq-item-${index}`}>
              <button
                className="w-full px-6 py-5 flex items-center justify-between text-left"
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              >
                <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-20 bg-yellow-500" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Ready to Start Your Project?</h2>
        <p className="text-xl text-slate-700 mb-8 max-w-2xl mx-auto">
          Get in touch with us today for a free consultation and quote
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/contact"
            className="bg-slate-900 hover:bg-slate-800 text-white px-10 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center justify-center"
            data-testid="cta-contact"
          >
            Contact Us Now
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
          <a
            href="tel:+919355744699"
            className="bg-transparent border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-10 py-4 rounded-lg font-semibold text-lg transition-all duration-300 inline-flex items-center justify-center"
            data-testid="cta-phone"
          >
            +91 9355744699
          </a>
        </div>
      </div>
    </section>
  );
};

// Main HomePage Component
export default function HomePage() {
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, projectsRes, clientsRes, statsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/projects`),
          axios.get(`${API}/clients`),
          axios.get(`${API}/stats`)
        ]);
        setServices(servicesRes.data);
        setProjects(projectsRes.data);
        setClients(clientsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    document.title = "Scaffolding Rental & Shuttering Solutions | 30+ Years | Amar Preet";
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      <SEO
        title="Premium Scaffolding & Shuttering Rental India"
        description="Amar Preet Scaffolding - Premium scaffolding & shuttering rental services across India. 30+ years experience. Delhi, Noida, Hyderabad, Vizag. Get free quote today."
        canonical="/"
      />
      <HeroSection />
      <StatsSection stats={stats} />
      <AboutSection />
      <ServicesSection services={services} />
      <ProjectsSection projects={projects} />
      <ClientsSection clients={clients} />
      <FAQSection />
      <CTASection />
    </div>
  );
}
